import {
    Cell,
    Slice,
    Address,
    Builder,
    beginCell,
    ComputeError,
    TupleItem,
    TupleReader,
    Dictionary,
    contractAddress,
    address,
    ContractProvider,
    Sender,
    Contract,
    ContractABI,
    ABIType,
    ABIGetter,
    ABIReceiver,
    TupleBuilder,
    DictionaryValue
} from '@ton/core';

export type DataSize = {
    $$type: 'DataSize';
    cells: bigint;
    bits: bigint;
    refs: bigint;
}

export function storeDataSize(src: DataSize) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.cells, 257);
        b_0.storeInt(src.bits, 257);
        b_0.storeInt(src.refs, 257);
    };
}

export function loadDataSize(slice: Slice) {
    const sc_0 = slice;
    const _cells = sc_0.loadIntBig(257);
    const _bits = sc_0.loadIntBig(257);
    const _refs = sc_0.loadIntBig(257);
    return { $$type: 'DataSize' as const, cells: _cells, bits: _bits, refs: _refs };
}

export function loadTupleDataSize(source: TupleReader) {
    const _cells = source.readBigNumber();
    const _bits = source.readBigNumber();
    const _refs = source.readBigNumber();
    return { $$type: 'DataSize' as const, cells: _cells, bits: _bits, refs: _refs };
}

export function loadGetterTupleDataSize(source: TupleReader) {
    const _cells = source.readBigNumber();
    const _bits = source.readBigNumber();
    const _refs = source.readBigNumber();
    return { $$type: 'DataSize' as const, cells: _cells, bits: _bits, refs: _refs };
}

export function storeTupleDataSize(source: DataSize) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.cells);
    builder.writeNumber(source.bits);
    builder.writeNumber(source.refs);
    return builder.build();
}

export function dictValueParserDataSize(): DictionaryValue<DataSize> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDataSize(src)).endCell());
        },
        parse: (src) => {
            return loadDataSize(src.loadRef().beginParse());
        }
    }
}

export type SignedBundle = {
    $$type: 'SignedBundle';
    signature: Buffer;
    signedData: Slice;
}

export function storeSignedBundle(src: SignedBundle) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBuffer(src.signature);
        b_0.storeBuilder(src.signedData.asBuilder());
    };
}

export function loadSignedBundle(slice: Slice) {
    const sc_0 = slice;
    const _signature = sc_0.loadBuffer(64);
    const _signedData = sc_0;
    return { $$type: 'SignedBundle' as const, signature: _signature, signedData: _signedData };
}

export function loadTupleSignedBundle(source: TupleReader) {
    const _signature = source.readBuffer();
    const _signedData = source.readCell().asSlice();
    return { $$type: 'SignedBundle' as const, signature: _signature, signedData: _signedData };
}

export function loadGetterTupleSignedBundle(source: TupleReader) {
    const _signature = source.readBuffer();
    const _signedData = source.readCell().asSlice();
    return { $$type: 'SignedBundle' as const, signature: _signature, signedData: _signedData };
}

export function storeTupleSignedBundle(source: SignedBundle) {
    const builder = new TupleBuilder();
    builder.writeBuffer(source.signature);
    builder.writeSlice(source.signedData.asCell());
    return builder.build();
}

export function dictValueParserSignedBundle(): DictionaryValue<SignedBundle> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeSignedBundle(src)).endCell());
        },
        parse: (src) => {
            return loadSignedBundle(src.loadRef().beginParse());
        }
    }
}

export type StateInit = {
    $$type: 'StateInit';
    code: Cell;
    data: Cell;
}

export function storeStateInit(src: StateInit) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeRef(src.code);
        b_0.storeRef(src.data);
    };
}

export function loadStateInit(slice: Slice) {
    const sc_0 = slice;
    const _code = sc_0.loadRef();
    const _data = sc_0.loadRef();
    return { $$type: 'StateInit' as const, code: _code, data: _data };
}

export function loadTupleStateInit(source: TupleReader) {
    const _code = source.readCell();
    const _data = source.readCell();
    return { $$type: 'StateInit' as const, code: _code, data: _data };
}

export function loadGetterTupleStateInit(source: TupleReader) {
    const _code = source.readCell();
    const _data = source.readCell();
    return { $$type: 'StateInit' as const, code: _code, data: _data };
}

export function storeTupleStateInit(source: StateInit) {
    const builder = new TupleBuilder();
    builder.writeCell(source.code);
    builder.writeCell(source.data);
    return builder.build();
}

export function dictValueParserStateInit(): DictionaryValue<StateInit> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeStateInit(src)).endCell());
        },
        parse: (src) => {
            return loadStateInit(src.loadRef().beginParse());
        }
    }
}

export type Context = {
    $$type: 'Context';
    bounceable: boolean;
    sender: Address;
    value: bigint;
    raw: Slice;
}

export function storeContext(src: Context) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBit(src.bounceable);
        b_0.storeAddress(src.sender);
        b_0.storeInt(src.value, 257);
        b_0.storeRef(src.raw.asCell());
    };
}

export function loadContext(slice: Slice) {
    const sc_0 = slice;
    const _bounceable = sc_0.loadBit();
    const _sender = sc_0.loadAddress();
    const _value = sc_0.loadIntBig(257);
    const _raw = sc_0.loadRef().asSlice();
    return { $$type: 'Context' as const, bounceable: _bounceable, sender: _sender, value: _value, raw: _raw };
}

export function loadTupleContext(source: TupleReader) {
    const _bounceable = source.readBoolean();
    const _sender = source.readAddress();
    const _value = source.readBigNumber();
    const _raw = source.readCell().asSlice();
    return { $$type: 'Context' as const, bounceable: _bounceable, sender: _sender, value: _value, raw: _raw };
}

export function loadGetterTupleContext(source: TupleReader) {
    const _bounceable = source.readBoolean();
    const _sender = source.readAddress();
    const _value = source.readBigNumber();
    const _raw = source.readCell().asSlice();
    return { $$type: 'Context' as const, bounceable: _bounceable, sender: _sender, value: _value, raw: _raw };
}

export function storeTupleContext(source: Context) {
    const builder = new TupleBuilder();
    builder.writeBoolean(source.bounceable);
    builder.writeAddress(source.sender);
    builder.writeNumber(source.value);
    builder.writeSlice(source.raw.asCell());
    return builder.build();
}

export function dictValueParserContext(): DictionaryValue<Context> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeContext(src)).endCell());
        },
        parse: (src) => {
            return loadContext(src.loadRef().beginParse());
        }
    }
}

export type SendParameters = {
    $$type: 'SendParameters';
    mode: bigint;
    body: Cell | null;
    code: Cell | null;
    data: Cell | null;
    value: bigint;
    to: Address;
    bounce: boolean;
}

export function storeSendParameters(src: SendParameters) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.mode, 257);
        if (src.body !== null && src.body !== undefined) { b_0.storeBit(true).storeRef(src.body); } else { b_0.storeBit(false); }
        if (src.code !== null && src.code !== undefined) { b_0.storeBit(true).storeRef(src.code); } else { b_0.storeBit(false); }
        if (src.data !== null && src.data !== undefined) { b_0.storeBit(true).storeRef(src.data); } else { b_0.storeBit(false); }
        b_0.storeInt(src.value, 257);
        b_0.storeAddress(src.to);
        b_0.storeBit(src.bounce);
    };
}

export function loadSendParameters(slice: Slice) {
    const sc_0 = slice;
    const _mode = sc_0.loadIntBig(257);
    const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _code = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _data = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _value = sc_0.loadIntBig(257);
    const _to = sc_0.loadAddress();
    const _bounce = sc_0.loadBit();
    return { $$type: 'SendParameters' as const, mode: _mode, body: _body, code: _code, data: _data, value: _value, to: _to, bounce: _bounce };
}

export function loadTupleSendParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _code = source.readCellOpt();
    const _data = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'SendParameters' as const, mode: _mode, body: _body, code: _code, data: _data, value: _value, to: _to, bounce: _bounce };
}

export function loadGetterTupleSendParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _code = source.readCellOpt();
    const _data = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'SendParameters' as const, mode: _mode, body: _body, code: _code, data: _data, value: _value, to: _to, bounce: _bounce };
}

export function storeTupleSendParameters(source: SendParameters) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.mode);
    builder.writeCell(source.body);
    builder.writeCell(source.code);
    builder.writeCell(source.data);
    builder.writeNumber(source.value);
    builder.writeAddress(source.to);
    builder.writeBoolean(source.bounce);
    return builder.build();
}

export function dictValueParserSendParameters(): DictionaryValue<SendParameters> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeSendParameters(src)).endCell());
        },
        parse: (src) => {
            return loadSendParameters(src.loadRef().beginParse());
        }
    }
}

export type MessageParameters = {
    $$type: 'MessageParameters';
    mode: bigint;
    body: Cell | null;
    value: bigint;
    to: Address;
    bounce: boolean;
}

export function storeMessageParameters(src: MessageParameters) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.mode, 257);
        if (src.body !== null && src.body !== undefined) { b_0.storeBit(true).storeRef(src.body); } else { b_0.storeBit(false); }
        b_0.storeInt(src.value, 257);
        b_0.storeAddress(src.to);
        b_0.storeBit(src.bounce);
    };
}

export function loadMessageParameters(slice: Slice) {
    const sc_0 = slice;
    const _mode = sc_0.loadIntBig(257);
    const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _value = sc_0.loadIntBig(257);
    const _to = sc_0.loadAddress();
    const _bounce = sc_0.loadBit();
    return { $$type: 'MessageParameters' as const, mode: _mode, body: _body, value: _value, to: _to, bounce: _bounce };
}

export function loadTupleMessageParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'MessageParameters' as const, mode: _mode, body: _body, value: _value, to: _to, bounce: _bounce };
}

export function loadGetterTupleMessageParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _to = source.readAddress();
    const _bounce = source.readBoolean();
    return { $$type: 'MessageParameters' as const, mode: _mode, body: _body, value: _value, to: _to, bounce: _bounce };
}

export function storeTupleMessageParameters(source: MessageParameters) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.mode);
    builder.writeCell(source.body);
    builder.writeNumber(source.value);
    builder.writeAddress(source.to);
    builder.writeBoolean(source.bounce);
    return builder.build();
}

export function dictValueParserMessageParameters(): DictionaryValue<MessageParameters> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeMessageParameters(src)).endCell());
        },
        parse: (src) => {
            return loadMessageParameters(src.loadRef().beginParse());
        }
    }
}

export type DeployParameters = {
    $$type: 'DeployParameters';
    mode: bigint;
    body: Cell | null;
    value: bigint;
    bounce: boolean;
    init: StateInit;
}

export function storeDeployParameters(src: DeployParameters) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.mode, 257);
        if (src.body !== null && src.body !== undefined) { b_0.storeBit(true).storeRef(src.body); } else { b_0.storeBit(false); }
        b_0.storeInt(src.value, 257);
        b_0.storeBit(src.bounce);
        b_0.store(storeStateInit(src.init));
    };
}

export function loadDeployParameters(slice: Slice) {
    const sc_0 = slice;
    const _mode = sc_0.loadIntBig(257);
    const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
    const _value = sc_0.loadIntBig(257);
    const _bounce = sc_0.loadBit();
    const _init = loadStateInit(sc_0);
    return { $$type: 'DeployParameters' as const, mode: _mode, body: _body, value: _value, bounce: _bounce, init: _init };
}

export function loadTupleDeployParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _bounce = source.readBoolean();
    const _init = loadTupleStateInit(source);
    return { $$type: 'DeployParameters' as const, mode: _mode, body: _body, value: _value, bounce: _bounce, init: _init };
}

export function loadGetterTupleDeployParameters(source: TupleReader) {
    const _mode = source.readBigNumber();
    const _body = source.readCellOpt();
    const _value = source.readBigNumber();
    const _bounce = source.readBoolean();
    const _init = loadGetterTupleStateInit(source);
    return { $$type: 'DeployParameters' as const, mode: _mode, body: _body, value: _value, bounce: _bounce, init: _init };
}

export function storeTupleDeployParameters(source: DeployParameters) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.mode);
    builder.writeCell(source.body);
    builder.writeNumber(source.value);
    builder.writeBoolean(source.bounce);
    builder.writeTuple(storeTupleStateInit(source.init));
    return builder.build();
}

export function dictValueParserDeployParameters(): DictionaryValue<DeployParameters> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDeployParameters(src)).endCell());
        },
        parse: (src) => {
            return loadDeployParameters(src.loadRef().beginParse());
        }
    }
}

export type StdAddress = {
    $$type: 'StdAddress';
    workchain: bigint;
    address: bigint;
}

export function storeStdAddress(src: StdAddress) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.workchain, 8);
        b_0.storeUint(src.address, 256);
    };
}

export function loadStdAddress(slice: Slice) {
    const sc_0 = slice;
    const _workchain = sc_0.loadIntBig(8);
    const _address = sc_0.loadUintBig(256);
    return { $$type: 'StdAddress' as const, workchain: _workchain, address: _address };
}

export function loadTupleStdAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readBigNumber();
    return { $$type: 'StdAddress' as const, workchain: _workchain, address: _address };
}

export function loadGetterTupleStdAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readBigNumber();
    return { $$type: 'StdAddress' as const, workchain: _workchain, address: _address };
}

export function storeTupleStdAddress(source: StdAddress) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.workchain);
    builder.writeNumber(source.address);
    return builder.build();
}

export function dictValueParserStdAddress(): DictionaryValue<StdAddress> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeStdAddress(src)).endCell());
        },
        parse: (src) => {
            return loadStdAddress(src.loadRef().beginParse());
        }
    }
}

export type VarAddress = {
    $$type: 'VarAddress';
    workchain: bigint;
    address: Slice;
}

export function storeVarAddress(src: VarAddress) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.workchain, 32);
        b_0.storeRef(src.address.asCell());
    };
}

export function loadVarAddress(slice: Slice) {
    const sc_0 = slice;
    const _workchain = sc_0.loadIntBig(32);
    const _address = sc_0.loadRef().asSlice();
    return { $$type: 'VarAddress' as const, workchain: _workchain, address: _address };
}

export function loadTupleVarAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readCell().asSlice();
    return { $$type: 'VarAddress' as const, workchain: _workchain, address: _address };
}

export function loadGetterTupleVarAddress(source: TupleReader) {
    const _workchain = source.readBigNumber();
    const _address = source.readCell().asSlice();
    return { $$type: 'VarAddress' as const, workchain: _workchain, address: _address };
}

export function storeTupleVarAddress(source: VarAddress) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.workchain);
    builder.writeSlice(source.address.asCell());
    return builder.build();
}

export function dictValueParserVarAddress(): DictionaryValue<VarAddress> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeVarAddress(src)).endCell());
        },
        parse: (src) => {
            return loadVarAddress(src.loadRef().beginParse());
        }
    }
}

export type BasechainAddress = {
    $$type: 'BasechainAddress';
    hash: bigint | null;
}

export function storeBasechainAddress(src: BasechainAddress) {
    return (builder: Builder) => {
        const b_0 = builder;
        if (src.hash !== null && src.hash !== undefined) { b_0.storeBit(true).storeInt(src.hash, 257); } else { b_0.storeBit(false); }
    };
}

export function loadBasechainAddress(slice: Slice) {
    const sc_0 = slice;
    const _hash = sc_0.loadBit() ? sc_0.loadIntBig(257) : null;
    return { $$type: 'BasechainAddress' as const, hash: _hash };
}

export function loadTupleBasechainAddress(source: TupleReader) {
    const _hash = source.readBigNumberOpt();
    return { $$type: 'BasechainAddress' as const, hash: _hash };
}

export function loadGetterTupleBasechainAddress(source: TupleReader) {
    const _hash = source.readBigNumberOpt();
    return { $$type: 'BasechainAddress' as const, hash: _hash };
}

export function storeTupleBasechainAddress(source: BasechainAddress) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.hash);
    return builder.build();
}

export function dictValueParserBasechainAddress(): DictionaryValue<BasechainAddress> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBasechainAddress(src)).endCell());
        },
        parse: (src) => {
            return loadBasechainAddress(src.loadRef().beginParse());
        }
    }
}

export type Deploy = {
    $$type: 'Deploy';
    queryId: bigint;
}

export function storeDeploy(src: Deploy) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2490013878, 32);
        b_0.storeUint(src.queryId, 64);
    };
}

export function loadDeploy(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2490013878) { throw Error('Invalid prefix'); }
    const _queryId = sc_0.loadUintBig(64);
    return { $$type: 'Deploy' as const, queryId: _queryId };
}

export function loadTupleDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'Deploy' as const, queryId: _queryId };
}

export function loadGetterTupleDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'Deploy' as const, queryId: _queryId };
}

export function storeTupleDeploy(source: Deploy) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.queryId);
    return builder.build();
}

export function dictValueParserDeploy(): DictionaryValue<Deploy> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDeploy(src)).endCell());
        },
        parse: (src) => {
            return loadDeploy(src.loadRef().beginParse());
        }
    }
}

export type DeployOk = {
    $$type: 'DeployOk';
    queryId: bigint;
}

export function storeDeployOk(src: DeployOk) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2952335191, 32);
        b_0.storeUint(src.queryId, 64);
    };
}

export function loadDeployOk(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2952335191) { throw Error('Invalid prefix'); }
    const _queryId = sc_0.loadUintBig(64);
    return { $$type: 'DeployOk' as const, queryId: _queryId };
}

export function loadTupleDeployOk(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'DeployOk' as const, queryId: _queryId };
}

export function loadGetterTupleDeployOk(source: TupleReader) {
    const _queryId = source.readBigNumber();
    return { $$type: 'DeployOk' as const, queryId: _queryId };
}

export function storeTupleDeployOk(source: DeployOk) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.queryId);
    return builder.build();
}

export function dictValueParserDeployOk(): DictionaryValue<DeployOk> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeDeployOk(src)).endCell());
        },
        parse: (src) => {
            return loadDeployOk(src.loadRef().beginParse());
        }
    }
}

export type FactoryDeploy = {
    $$type: 'FactoryDeploy';
    queryId: bigint;
    cashback: Address;
}

export function storeFactoryDeploy(src: FactoryDeploy) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1829761339, 32);
        b_0.storeUint(src.queryId, 64);
        b_0.storeAddress(src.cashback);
    };
}

export function loadFactoryDeploy(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1829761339) { throw Error('Invalid prefix'); }
    const _queryId = sc_0.loadUintBig(64);
    const _cashback = sc_0.loadAddress();
    return { $$type: 'FactoryDeploy' as const, queryId: _queryId, cashback: _cashback };
}

export function loadTupleFactoryDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    const _cashback = source.readAddress();
    return { $$type: 'FactoryDeploy' as const, queryId: _queryId, cashback: _cashback };
}

export function loadGetterTupleFactoryDeploy(source: TupleReader) {
    const _queryId = source.readBigNumber();
    const _cashback = source.readAddress();
    return { $$type: 'FactoryDeploy' as const, queryId: _queryId, cashback: _cashback };
}

export function storeTupleFactoryDeploy(source: FactoryDeploy) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.queryId);
    builder.writeAddress(source.cashback);
    return builder.build();
}

export function dictValueParserFactoryDeploy(): DictionaryValue<FactoryDeploy> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeFactoryDeploy(src)).endCell());
        },
        parse: (src) => {
            return loadFactoryDeploy(src.loadRef().beginParse());
        }
    }
}

export type OwnerSetBasePrice = {
    $$type: 'OwnerSetBasePrice';
    newPrice: bigint;
}

export function storeOwnerSetBasePrice(src: OwnerSetBasePrice) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1268395613, 32);
        b_0.storeInt(src.newPrice, 257);
    };
}

export function loadOwnerSetBasePrice(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1268395613) { throw Error('Invalid prefix'); }
    const _newPrice = sc_0.loadIntBig(257);
    return { $$type: 'OwnerSetBasePrice' as const, newPrice: _newPrice };
}

export function loadTupleOwnerSetBasePrice(source: TupleReader) {
    const _newPrice = source.readBigNumber();
    return { $$type: 'OwnerSetBasePrice' as const, newPrice: _newPrice };
}

export function loadGetterTupleOwnerSetBasePrice(source: TupleReader) {
    const _newPrice = source.readBigNumber();
    return { $$type: 'OwnerSetBasePrice' as const, newPrice: _newPrice };
}

export function storeTupleOwnerSetBasePrice(source: OwnerSetBasePrice) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.newPrice);
    return builder.build();
}

export function dictValueParserOwnerSetBasePrice(): DictionaryValue<OwnerSetBasePrice> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerSetBasePrice(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerSetBasePrice(src.loadRef().beginParse());
        }
    }
}

export type OwnerSetLevelMultiplier = {
    $$type: 'OwnerSetLevelMultiplier';
    level: bigint;
    k: bigint;
}

export function storeOwnerSetLevelMultiplier(src: OwnerSetLevelMultiplier) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2399467553, 32);
        b_0.storeInt(src.level, 257);
        b_0.storeInt(src.k, 257);
    };
}

export function loadOwnerSetLevelMultiplier(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2399467553) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    const _k = sc_0.loadIntBig(257);
    return { $$type: 'OwnerSetLevelMultiplier' as const, level: _level, k: _k };
}

export function loadTupleOwnerSetLevelMultiplier(source: TupleReader) {
    const _level = source.readBigNumber();
    const _k = source.readBigNumber();
    return { $$type: 'OwnerSetLevelMultiplier' as const, level: _level, k: _k };
}

export function loadGetterTupleOwnerSetLevelMultiplier(source: TupleReader) {
    const _level = source.readBigNumber();
    const _k = source.readBigNumber();
    return { $$type: 'OwnerSetLevelMultiplier' as const, level: _level, k: _k };
}

export function storeTupleOwnerSetLevelMultiplier(source: OwnerSetLevelMultiplier) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    builder.writeNumber(source.k);
    return builder.build();
}

export function dictValueParserOwnerSetLevelMultiplier(): DictionaryValue<OwnerSetLevelMultiplier> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerSetLevelMultiplier(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerSetLevelMultiplier(src.loadRef().beginParse());
        }
    }
}

export type OwnerWithdraw = {
    $$type: 'OwnerWithdraw';
    amount: bigint;
    recipient: Address;
}

export function storeOwnerWithdraw(src: OwnerWithdraw) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2082387357, 32);
        b_0.storeInt(src.amount, 257);
        b_0.storeAddress(src.recipient);
    };
}

export function loadOwnerWithdraw(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2082387357) { throw Error('Invalid prefix'); }
    const _amount = sc_0.loadIntBig(257);
    const _recipient = sc_0.loadAddress();
    return { $$type: 'OwnerWithdraw' as const, amount: _amount, recipient: _recipient };
}

export function loadTupleOwnerWithdraw(source: TupleReader) {
    const _amount = source.readBigNumber();
    const _recipient = source.readAddress();
    return { $$type: 'OwnerWithdraw' as const, amount: _amount, recipient: _recipient };
}

export function loadGetterTupleOwnerWithdraw(source: TupleReader) {
    const _amount = source.readBigNumber();
    const _recipient = source.readAddress();
    return { $$type: 'OwnerWithdraw' as const, amount: _amount, recipient: _recipient };
}

export function storeTupleOwnerWithdraw(source: OwnerWithdraw) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.amount);
    builder.writeAddress(source.recipient);
    return builder.build();
}

export function dictValueParserOwnerWithdraw(): DictionaryValue<OwnerWithdraw> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerWithdraw(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerWithdraw(src.loadRef().beginParse());
        }
    }
}

export type OwnerTransferOwnership = {
    $$type: 'OwnerTransferOwnership';
    newOwner: Address;
}

export function storeOwnerTransferOwnership(src: OwnerTransferOwnership) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2073089375, 32);
        b_0.storeAddress(src.newOwner);
    };
}

export function loadOwnerTransferOwnership(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2073089375) { throw Error('Invalid prefix'); }
    const _newOwner = sc_0.loadAddress();
    return { $$type: 'OwnerTransferOwnership' as const, newOwner: _newOwner };
}

export function loadTupleOwnerTransferOwnership(source: TupleReader) {
    const _newOwner = source.readAddress();
    return { $$type: 'OwnerTransferOwnership' as const, newOwner: _newOwner };
}

export function loadGetterTupleOwnerTransferOwnership(source: TupleReader) {
    const _newOwner = source.readAddress();
    return { $$type: 'OwnerTransferOwnership' as const, newOwner: _newOwner };
}

export function storeTupleOwnerTransferOwnership(source: OwnerTransferOwnership) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.newOwner);
    return builder.build();
}

export function dictValueParserOwnerTransferOwnership(): DictionaryValue<OwnerTransferOwnership> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerTransferOwnership(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerTransferOwnership(src.loadRef().beginParse());
        }
    }
}

export type OwnerActivateMega = {
    $$type: 'OwnerActivateMega';
    ticketCount: bigint;
    prizeAmount: bigint;
}

export function storeOwnerActivateMega(src: OwnerActivateMega) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2107791225, 32);
        b_0.storeInt(src.ticketCount, 257);
        b_0.storeInt(src.prizeAmount, 257);
    };
}

export function loadOwnerActivateMega(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2107791225) { throw Error('Invalid prefix'); }
    const _ticketCount = sc_0.loadIntBig(257);
    const _prizeAmount = sc_0.loadIntBig(257);
    return { $$type: 'OwnerActivateMega' as const, ticketCount: _ticketCount, prizeAmount: _prizeAmount };
}

export function loadTupleOwnerActivateMega(source: TupleReader) {
    const _ticketCount = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    return { $$type: 'OwnerActivateMega' as const, ticketCount: _ticketCount, prizeAmount: _prizeAmount };
}

export function loadGetterTupleOwnerActivateMega(source: TupleReader) {
    const _ticketCount = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    return { $$type: 'OwnerActivateMega' as const, ticketCount: _ticketCount, prizeAmount: _prizeAmount };
}

export function storeTupleOwnerActivateMega(source: OwnerActivateMega) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.ticketCount);
    builder.writeNumber(source.prizeAmount);
    return builder.build();
}

export function dictValueParserOwnerActivateMega(): DictionaryValue<OwnerActivateMega> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeOwnerActivateMega(src)).endCell());
        },
        parse: (src) => {
            return loadOwnerActivateMega(src.loadRef().beginParse());
        }
    }
}

export type LevelConfig = {
    $$type: 'LevelConfig';
    k: bigint;
}

export function storeLevelConfig(src: LevelConfig) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.k, 257);
    };
}

export function loadLevelConfig(slice: Slice) {
    const sc_0 = slice;
    const _k = sc_0.loadIntBig(257);
    return { $$type: 'LevelConfig' as const, k: _k };
}

export function loadTupleLevelConfig(source: TupleReader) {
    const _k = source.readBigNumber();
    return { $$type: 'LevelConfig' as const, k: _k };
}

export function loadGetterTupleLevelConfig(source: TupleReader) {
    const _k = source.readBigNumber();
    return { $$type: 'LevelConfig' as const, k: _k };
}

export function storeTupleLevelConfig(source: LevelConfig) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.k);
    return builder.build();
}

export function dictValueParserLevelConfig(): DictionaryValue<LevelConfig> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeLevelConfig(src)).endCell());
        },
        parse: (src) => {
            return loadLevelConfig(src.loadRef().beginParse());
        }
    }
}

export type Cycle = {
    $$type: 'Cycle';
    id: bigint;
    ticketsSold: bigint;
    isFinished: boolean;
    tickets: Dictionary<bigint, Address>;
}

export function storeCycle(src: Cycle) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.id, 257);
        b_0.storeInt(src.ticketsSold, 257);
        b_0.storeBit(src.isFinished);
        b_0.storeDict(src.tickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address());
    };
}

export function loadCycle(slice: Slice) {
    const sc_0 = slice;
    const _id = sc_0.loadIntBig(257);
    const _ticketsSold = sc_0.loadIntBig(257);
    const _isFinished = sc_0.loadBit();
    const _tickets = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), sc_0);
    return { $$type: 'Cycle' as const, id: _id, ticketsSold: _ticketsSold, isFinished: _isFinished, tickets: _tickets };
}

export function loadTupleCycle(source: TupleReader) {
    const _id = source.readBigNumber();
    const _ticketsSold = source.readBigNumber();
    const _isFinished = source.readBoolean();
    const _tickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    return { $$type: 'Cycle' as const, id: _id, ticketsSold: _ticketsSold, isFinished: _isFinished, tickets: _tickets };
}

export function loadGetterTupleCycle(source: TupleReader) {
    const _id = source.readBigNumber();
    const _ticketsSold = source.readBigNumber();
    const _isFinished = source.readBoolean();
    const _tickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    return { $$type: 'Cycle' as const, id: _id, ticketsSold: _ticketsSold, isFinished: _isFinished, tickets: _tickets };
}

export function storeTupleCycle(source: Cycle) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.id);
    builder.writeNumber(source.ticketsSold);
    builder.writeBoolean(source.isFinished);
    builder.writeCell(source.tickets.size > 0 ? beginCell().storeDictDirect(source.tickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address()).endCell() : null);
    return builder.build();
}

export function dictValueParserCycle(): DictionaryValue<Cycle> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeCycle(src)).endCell());
        },
        parse: (src) => {
            return loadCycle(src.loadRef().beginParse());
        }
    }
}

export type UserTickets = {
    $$type: 'UserTickets';
    cycleId: bigint;
    count: bigint;
    ticketsOwned: Dictionary<bigint, boolean>;
}

export function storeUserTickets(src: UserTickets) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.cycleId, 257);
        b_0.storeInt(src.count, 257);
        b_0.storeDict(src.ticketsOwned, Dictionary.Keys.BigInt(257), Dictionary.Values.Bool());
    };
}

export function loadUserTickets(slice: Slice) {
    const sc_0 = slice;
    const _cycleId = sc_0.loadIntBig(257);
    const _count = sc_0.loadIntBig(257);
    const _ticketsOwned = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.Bool(), sc_0);
    return { $$type: 'UserTickets' as const, cycleId: _cycleId, count: _count, ticketsOwned: _ticketsOwned };
}

export function loadTupleUserTickets(source: TupleReader) {
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    const _ticketsOwned = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Bool(), source.readCellOpt());
    return { $$type: 'UserTickets' as const, cycleId: _cycleId, count: _count, ticketsOwned: _ticketsOwned };
}

export function loadGetterTupleUserTickets(source: TupleReader) {
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    const _ticketsOwned = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Bool(), source.readCellOpt());
    return { $$type: 'UserTickets' as const, cycleId: _cycleId, count: _count, ticketsOwned: _ticketsOwned };
}

export function storeTupleUserTickets(source: UserTickets) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.cycleId);
    builder.writeNumber(source.count);
    builder.writeCell(source.ticketsOwned.size > 0 ? beginCell().storeDictDirect(source.ticketsOwned, Dictionary.Keys.BigInt(257), Dictionary.Values.Bool()).endCell() : null);
    return builder.build();
}

export function dictValueParserUserTickets(): DictionaryValue<UserTickets> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeUserTickets(src)).endCell());
        },
        parse: (src) => {
            return loadUserTickets(src.loadRef().beginParse());
        }
    }
}

export type PlayerInfo = {
    $$type: 'PlayerInfo';
    referrer: Address;
    userLevel: bigint;
    winningJackpotAmount: bigint;
    ticketsBought: Dictionary<bigint, UserTickets>;
    referredCount: bigint;
    consecutiveWins: Dictionary<bigint, bigint>;
    lastWinCycle: Dictionary<bigint, bigint>;
    inRowBonus: bigint;
    buyBonus: Dictionary<bigint, bigint>;
    soldBonus: bigint;
    referralBonus: bigint;
    ticketsSoldByReferrals: bigint;
    megaTicketsBought: UserTickets;
}

export function storePlayerInfo(src: PlayerInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeAddress(src.referrer);
        b_0.storeInt(src.userLevel, 257);
        b_0.storeInt(src.winningJackpotAmount, 257);
        b_0.storeDict(src.ticketsBought, Dictionary.Keys.BigInt(257), dictValueParserUserTickets());
        const b_1 = new Builder();
        b_1.storeInt(src.referredCount, 257);
        b_1.storeDict(src.consecutiveWins, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_1.storeDict(src.lastWinCycle, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_1.storeInt(src.inRowBonus, 257);
        b_1.storeDict(src.buyBonus, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_1.storeInt(src.soldBonus, 257);
        const b_2 = new Builder();
        b_2.storeInt(src.referralBonus, 257);
        b_2.storeInt(src.ticketsSoldByReferrals, 257);
        const b_3 = new Builder();
        b_3.store(storeUserTickets(src.megaTicketsBought));
        b_2.storeRef(b_3.endCell());
        b_1.storeRef(b_2.endCell());
        b_0.storeRef(b_1.endCell());
    };
}

export function loadPlayerInfo(slice: Slice) {
    const sc_0 = slice;
    const _referrer = sc_0.loadAddress();
    const _userLevel = sc_0.loadIntBig(257);
    const _winningJackpotAmount = sc_0.loadIntBig(257);
    const _ticketsBought = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserUserTickets(), sc_0);
    const sc_1 = sc_0.loadRef().beginParse();
    const _referredCount = sc_1.loadIntBig(257);
    const _consecutiveWins = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_1);
    const _lastWinCycle = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_1);
    const _inRowBonus = sc_1.loadIntBig(257);
    const _buyBonus = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_1);
    const _soldBonus = sc_1.loadIntBig(257);
    const sc_2 = sc_1.loadRef().beginParse();
    const _referralBonus = sc_2.loadIntBig(257);
    const _ticketsSoldByReferrals = sc_2.loadIntBig(257);
    const sc_3 = sc_2.loadRef().beginParse();
    const _megaTicketsBought = loadUserTickets(sc_3);
    return { $$type: 'PlayerInfo' as const, referrer: _referrer, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, ticketsBought: _ticketsBought, referredCount: _referredCount, consecutiveWins: _consecutiveWins, lastWinCycle: _lastWinCycle, inRowBonus: _inRowBonus, buyBonus: _buyBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals, megaTicketsBought: _megaTicketsBought };
}

export function loadTuplePlayerInfo(source: TupleReader) {
    const _referrer = source.readAddress();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _ticketsBought = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserUserTickets(), source.readCellOpt());
    const _referredCount = source.readBigNumber();
    const _consecutiveWins = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _lastWinCycle = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _inRowBonus = source.readBigNumber();
    const _buyBonus = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    const _megaTicketsBought = loadTupleUserTickets(source);
    return { $$type: 'PlayerInfo' as const, referrer: _referrer, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, ticketsBought: _ticketsBought, referredCount: _referredCount, consecutiveWins: _consecutiveWins, lastWinCycle: _lastWinCycle, inRowBonus: _inRowBonus, buyBonus: _buyBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals, megaTicketsBought: _megaTicketsBought };
}

export function loadGetterTuplePlayerInfo(source: TupleReader) {
    const _referrer = source.readAddress();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _ticketsBought = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserUserTickets(), source.readCellOpt());
    const _referredCount = source.readBigNumber();
    const _consecutiveWins = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _lastWinCycle = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _inRowBonus = source.readBigNumber();
    const _buyBonus = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    const _megaTicketsBought = loadGetterTupleUserTickets(source);
    return { $$type: 'PlayerInfo' as const, referrer: _referrer, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, ticketsBought: _ticketsBought, referredCount: _referredCount, consecutiveWins: _consecutiveWins, lastWinCycle: _lastWinCycle, inRowBonus: _inRowBonus, buyBonus: _buyBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals, megaTicketsBought: _megaTicketsBought };
}

export function storeTuplePlayerInfo(source: PlayerInfo) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.referrer);
    builder.writeNumber(source.userLevel);
    builder.writeNumber(source.winningJackpotAmount);
    builder.writeCell(source.ticketsBought.size > 0 ? beginCell().storeDictDirect(source.ticketsBought, Dictionary.Keys.BigInt(257), dictValueParserUserTickets()).endCell() : null);
    builder.writeNumber(source.referredCount);
    builder.writeCell(source.consecutiveWins.size > 0 ? beginCell().storeDictDirect(source.consecutiveWins, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeCell(source.lastWinCycle.size > 0 ? beginCell().storeDictDirect(source.lastWinCycle, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeNumber(source.inRowBonus);
    builder.writeCell(source.buyBonus.size > 0 ? beginCell().storeDictDirect(source.buyBonus, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeNumber(source.soldBonus);
    builder.writeNumber(source.referralBonus);
    builder.writeNumber(source.ticketsSoldByReferrals);
    builder.writeTuple(storeTupleUserTickets(source.megaTicketsBought));
    return builder.build();
}

export function dictValueParserPlayerInfo(): DictionaryValue<PlayerInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePlayerInfo(src)).endCell());
        },
        parse: (src) => {
            return loadPlayerInfo(src.loadRef().beginParse());
        }
    }
}

export type WinningTicketsList = {
    $$type: 'WinningTicketsList';
    winningTicketIds: Dictionary<bigint, bigint>;
    winningTicketAmounts: Dictionary<bigint, bigint>;
}

export function storeWinningTicketsList(src: WinningTicketsList) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeDict(src.winningTicketIds, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
        b_0.storeDict(src.winningTicketAmounts, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257));
    };
}

export function loadWinningTicketsList(slice: Slice) {
    const sc_0 = slice;
    const _winningTicketIds = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_0);
    const _winningTicketAmounts = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), sc_0);
    return { $$type: 'WinningTicketsList' as const, winningTicketIds: _winningTicketIds, winningTicketAmounts: _winningTicketAmounts };
}

export function loadTupleWinningTicketsList(source: TupleReader) {
    const _winningTicketIds = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _winningTicketAmounts = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    return { $$type: 'WinningTicketsList' as const, winningTicketIds: _winningTicketIds, winningTicketAmounts: _winningTicketAmounts };
}

export function loadGetterTupleWinningTicketsList(source: TupleReader) {
    const _winningTicketIds = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    const _winningTicketAmounts = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
    return { $$type: 'WinningTicketsList' as const, winningTicketIds: _winningTicketIds, winningTicketAmounts: _winningTicketAmounts };
}

export function storeTupleWinningTicketsList(source: WinningTicketsList) {
    const builder = new TupleBuilder();
    builder.writeCell(source.winningTicketIds.size > 0 ? beginCell().storeDictDirect(source.winningTicketIds, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    builder.writeCell(source.winningTicketAmounts.size > 0 ? beginCell().storeDictDirect(source.winningTicketAmounts, Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257)).endCell() : null);
    return builder.build();
}

export function dictValueParserWinningTicketsList(): DictionaryValue<WinningTicketsList> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeWinningTicketsList(src)).endCell());
        },
        parse: (src) => {
            return loadWinningTicketsList(src.loadRef().beginParse());
        }
    }
}

export type PlayerBasicInfo = {
    $$type: 'PlayerBasicInfo';
    exists: boolean;
    userLevel: bigint;
    winningJackpotAmount: bigint;
    referredCount: bigint;
    inRowBonus: bigint;
    soldBonus: bigint;
    referralBonus: bigint;
    ticketsSoldByReferrals: bigint;
}

export function storePlayerBasicInfo(src: PlayerBasicInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBit(src.exists);
        b_0.storeInt(src.userLevel, 257);
        b_0.storeInt(src.winningJackpotAmount, 257);
        b_0.storeInt(src.referredCount, 257);
        const b_1 = new Builder();
        b_1.storeInt(src.inRowBonus, 257);
        b_1.storeInt(src.soldBonus, 257);
        b_1.storeInt(src.referralBonus, 257);
        const b_2 = new Builder();
        b_2.storeInt(src.ticketsSoldByReferrals, 257);
        b_1.storeRef(b_2.endCell());
        b_0.storeRef(b_1.endCell());
    };
}

export function loadPlayerBasicInfo(slice: Slice) {
    const sc_0 = slice;
    const _exists = sc_0.loadBit();
    const _userLevel = sc_0.loadIntBig(257);
    const _winningJackpotAmount = sc_0.loadIntBig(257);
    const _referredCount = sc_0.loadIntBig(257);
    const sc_1 = sc_0.loadRef().beginParse();
    const _inRowBonus = sc_1.loadIntBig(257);
    const _soldBonus = sc_1.loadIntBig(257);
    const _referralBonus = sc_1.loadIntBig(257);
    const sc_2 = sc_1.loadRef().beginParse();
    const _ticketsSoldByReferrals = sc_2.loadIntBig(257);
    return { $$type: 'PlayerBasicInfo' as const, exists: _exists, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, referredCount: _referredCount, inRowBonus: _inRowBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals };
}

export function loadTuplePlayerBasicInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _referredCount = source.readBigNumber();
    const _inRowBonus = source.readBigNumber();
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    return { $$type: 'PlayerBasicInfo' as const, exists: _exists, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, referredCount: _referredCount, inRowBonus: _inRowBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals };
}

export function loadGetterTuplePlayerBasicInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _userLevel = source.readBigNumber();
    const _winningJackpotAmount = source.readBigNumber();
    const _referredCount = source.readBigNumber();
    const _inRowBonus = source.readBigNumber();
    const _soldBonus = source.readBigNumber();
    const _referralBonus = source.readBigNumber();
    const _ticketsSoldByReferrals = source.readBigNumber();
    return { $$type: 'PlayerBasicInfo' as const, exists: _exists, userLevel: _userLevel, winningJackpotAmount: _winningJackpotAmount, referredCount: _referredCount, inRowBonus: _inRowBonus, soldBonus: _soldBonus, referralBonus: _referralBonus, ticketsSoldByReferrals: _ticketsSoldByReferrals };
}

export function storeTuplePlayerBasicInfo(source: PlayerBasicInfo) {
    const builder = new TupleBuilder();
    builder.writeBoolean(source.exists);
    builder.writeNumber(source.userLevel);
    builder.writeNumber(source.winningJackpotAmount);
    builder.writeNumber(source.referredCount);
    builder.writeNumber(source.inRowBonus);
    builder.writeNumber(source.soldBonus);
    builder.writeNumber(source.referralBonus);
    builder.writeNumber(source.ticketsSoldByReferrals);
    return builder.build();
}

export function dictValueParserPlayerBasicInfo(): DictionaryValue<PlayerBasicInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePlayerBasicInfo(src)).endCell());
        },
        parse: (src) => {
            return loadPlayerBasicInfo(src.loadRef().beginParse());
        }
    }
}

export type PlayerTicketsInfo = {
    $$type: 'PlayerTicketsInfo';
    exists: boolean;
    cycleId: bigint;
    count: bigint;
}

export function storePlayerTicketsInfo(src: PlayerTicketsInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeBit(src.exists);
        b_0.storeInt(src.cycleId, 257);
        b_0.storeInt(src.count, 257);
    };
}

export function loadPlayerTicketsInfo(slice: Slice) {
    const sc_0 = slice;
    const _exists = sc_0.loadBit();
    const _cycleId = sc_0.loadIntBig(257);
    const _count = sc_0.loadIntBig(257);
    return { $$type: 'PlayerTicketsInfo' as const, exists: _exists, cycleId: _cycleId, count: _count };
}

export function loadTuplePlayerTicketsInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'PlayerTicketsInfo' as const, exists: _exists, cycleId: _cycleId, count: _count };
}

export function loadGetterTuplePlayerTicketsInfo(source: TupleReader) {
    const _exists = source.readBoolean();
    const _cycleId = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'PlayerTicketsInfo' as const, exists: _exists, cycleId: _cycleId, count: _count };
}

export function storeTuplePlayerTicketsInfo(source: PlayerTicketsInfo) {
    const builder = new TupleBuilder();
    builder.writeBoolean(source.exists);
    builder.writeNumber(source.cycleId);
    builder.writeNumber(source.count);
    return builder.build();
}

export function dictValueParserPlayerTicketsInfo(): DictionaryValue<PlayerTicketsInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storePlayerTicketsInfo(src)).endCell());
        },
        parse: (src) => {
            return loadPlayerTicketsInfo(src.loadRef().beginParse());
        }
    }
}

export type MegaJackpotInfo = {
    $$type: 'MegaJackpotInfo';
    balance: bigint;
    isActive: boolean;
    ticketsSold: bigint;
    ticketsRequired: bigint;
    ticketPrice: bigint;
    prizeAmount: bigint;
    threshold: bigint;
}

export function storeMegaJackpotInfo(src: MegaJackpotInfo) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeInt(src.balance, 257);
        b_0.storeBit(src.isActive);
        b_0.storeInt(src.ticketsSold, 257);
        b_0.storeInt(src.ticketsRequired, 257);
        const b_1 = new Builder();
        b_1.storeInt(src.ticketPrice, 257);
        b_1.storeInt(src.prizeAmount, 257);
        b_1.storeInt(src.threshold, 257);
        b_0.storeRef(b_1.endCell());
    };
}

export function loadMegaJackpotInfo(slice: Slice) {
    const sc_0 = slice;
    const _balance = sc_0.loadIntBig(257);
    const _isActive = sc_0.loadBit();
    const _ticketsSold = sc_0.loadIntBig(257);
    const _ticketsRequired = sc_0.loadIntBig(257);
    const sc_1 = sc_0.loadRef().beginParse();
    const _ticketPrice = sc_1.loadIntBig(257);
    const _prizeAmount = sc_1.loadIntBig(257);
    const _threshold = sc_1.loadIntBig(257);
    return { $$type: 'MegaJackpotInfo' as const, balance: _balance, isActive: _isActive, ticketsSold: _ticketsSold, ticketsRequired: _ticketsRequired, ticketPrice: _ticketPrice, prizeAmount: _prizeAmount, threshold: _threshold };
}

export function loadTupleMegaJackpotInfo(source: TupleReader) {
    const _balance = source.readBigNumber();
    const _isActive = source.readBoolean();
    const _ticketsSold = source.readBigNumber();
    const _ticketsRequired = source.readBigNumber();
    const _ticketPrice = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    const _threshold = source.readBigNumber();
    return { $$type: 'MegaJackpotInfo' as const, balance: _balance, isActive: _isActive, ticketsSold: _ticketsSold, ticketsRequired: _ticketsRequired, ticketPrice: _ticketPrice, prizeAmount: _prizeAmount, threshold: _threshold };
}

export function loadGetterTupleMegaJackpotInfo(source: TupleReader) {
    const _balance = source.readBigNumber();
    const _isActive = source.readBoolean();
    const _ticketsSold = source.readBigNumber();
    const _ticketsRequired = source.readBigNumber();
    const _ticketPrice = source.readBigNumber();
    const _prizeAmount = source.readBigNumber();
    const _threshold = source.readBigNumber();
    return { $$type: 'MegaJackpotInfo' as const, balance: _balance, isActive: _isActive, ticketsSold: _ticketsSold, ticketsRequired: _ticketsRequired, ticketPrice: _ticketPrice, prizeAmount: _prizeAmount, threshold: _threshold };
}

export function storeTupleMegaJackpotInfo(source: MegaJackpotInfo) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.balance);
    builder.writeBoolean(source.isActive);
    builder.writeNumber(source.ticketsSold);
    builder.writeNumber(source.ticketsRequired);
    builder.writeNumber(source.ticketPrice);
    builder.writeNumber(source.prizeAmount);
    builder.writeNumber(source.threshold);
    return builder.build();
}

export function dictValueParserMegaJackpotInfo(): DictionaryValue<MegaJackpotInfo> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeMegaJackpotInfo(src)).endCell());
        },
        parse: (src) => {
            return loadMegaJackpotInfo(src.loadRef().beginParse());
        }
    }
}

export type Register = {
    $$type: 'Register';
    user: Address;
    referrer: Address;
}

export function storeRegister(src: Register) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(2206064315, 32);
        b_0.storeAddress(src.user);
        b_0.storeAddress(src.referrer);
    };
}

export function loadRegister(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 2206064315) { throw Error('Invalid prefix'); }
    const _user = sc_0.loadAddress();
    const _referrer = sc_0.loadAddress();
    return { $$type: 'Register' as const, user: _user, referrer: _referrer };
}

export function loadTupleRegister(source: TupleReader) {
    const _user = source.readAddress();
    const _referrer = source.readAddress();
    return { $$type: 'Register' as const, user: _user, referrer: _referrer };
}

export function loadGetterTupleRegister(source: TupleReader) {
    const _user = source.readAddress();
    const _referrer = source.readAddress();
    return { $$type: 'Register' as const, user: _user, referrer: _referrer };
}

export function storeTupleRegister(source: Register) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.user);
    builder.writeAddress(source.referrer);
    return builder.build();
}

export function dictValueParserRegister(): DictionaryValue<Register> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeRegister(src)).endCell());
        },
        parse: (src) => {
            return loadRegister(src.loadRef().beginParse());
        }
    }
}

export type BuyTicket = {
    $$type: 'BuyTicket';
    level: bigint;
    count: bigint;
}

export function storeBuyTicket(src: BuyTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(264192202, 32);
        b_0.storeInt(src.level, 257);
        b_0.storeInt(src.count, 257);
    };
}

export function loadBuyTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 264192202) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    const _count = sc_0.loadIntBig(257);
    return { $$type: 'BuyTicket' as const, level: _level, count: _count };
}

export function loadTupleBuyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'BuyTicket' as const, level: _level, count: _count };
}

export function loadGetterTupleBuyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _count = source.readBigNumber();
    return { $$type: 'BuyTicket' as const, level: _level, count: _count };
}

export function storeTupleBuyTicket(source: BuyTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    builder.writeNumber(source.count);
    return builder.build();
}

export function dictValueParserBuyTicket(): DictionaryValue<BuyTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyTicket(src.loadRef().beginParse());
        }
    }
}

export type BuyLuckyTicket = {
    $$type: 'BuyLuckyTicket';
    level: bigint;
    ticketId: bigint;
}

export function storeBuyLuckyTicket(src: BuyLuckyTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(1049254189, 32);
        b_0.storeInt(src.level, 257);
        b_0.storeInt(src.ticketId, 257);
    };
}

export function loadBuyLuckyTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 1049254189) { throw Error('Invalid prefix'); }
    const _level = sc_0.loadIntBig(257);
    const _ticketId = sc_0.loadIntBig(257);
    return { $$type: 'BuyLuckyTicket' as const, level: _level, ticketId: _ticketId };
}

export function loadTupleBuyLuckyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyTicket' as const, level: _level, ticketId: _ticketId };
}

export function loadGetterTupleBuyLuckyTicket(source: TupleReader) {
    const _level = source.readBigNumber();
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyTicket' as const, level: _level, ticketId: _ticketId };
}

export function storeTupleBuyLuckyTicket(source: BuyLuckyTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.level);
    builder.writeNumber(source.ticketId);
    return builder.build();
}

export function dictValueParserBuyLuckyTicket(): DictionaryValue<BuyLuckyTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyLuckyTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyLuckyTicket(src.loadRef().beginParse());
        }
    }
}

export type BuyMegaTicket = {
    $$type: 'BuyMegaTicket';
    count: bigint;
}

export function storeBuyMegaTicket(src: BuyMegaTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(764815221, 32);
        b_0.storeInt(src.count, 257);
    };
}

export function loadBuyMegaTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 764815221) { throw Error('Invalid prefix'); }
    const _count = sc_0.loadIntBig(257);
    return { $$type: 'BuyMegaTicket' as const, count: _count };
}

export function loadTupleBuyMegaTicket(source: TupleReader) {
    const _count = source.readBigNumber();
    return { $$type: 'BuyMegaTicket' as const, count: _count };
}

export function loadGetterTupleBuyMegaTicket(source: TupleReader) {
    const _count = source.readBigNumber();
    return { $$type: 'BuyMegaTicket' as const, count: _count };
}

export function storeTupleBuyMegaTicket(source: BuyMegaTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.count);
    return builder.build();
}

export function dictValueParserBuyMegaTicket(): DictionaryValue<BuyMegaTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyMegaTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyMegaTicket(src.loadRef().beginParse());
        }
    }
}

export type BuyLuckyMegaTicket = {
    $$type: 'BuyLuckyMegaTicket';
    ticketId: bigint;
}

export function storeBuyLuckyMegaTicket(src: BuyLuckyMegaTicket) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeUint(4112755696, 32);
        b_0.storeInt(src.ticketId, 257);
    };
}

export function loadBuyLuckyMegaTicket(slice: Slice) {
    const sc_0 = slice;
    if (sc_0.loadUint(32) !== 4112755696) { throw Error('Invalid prefix'); }
    const _ticketId = sc_0.loadIntBig(257);
    return { $$type: 'BuyLuckyMegaTicket' as const, ticketId: _ticketId };
}

export function loadTupleBuyLuckyMegaTicket(source: TupleReader) {
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyMegaTicket' as const, ticketId: _ticketId };
}

export function loadGetterTupleBuyLuckyMegaTicket(source: TupleReader) {
    const _ticketId = source.readBigNumber();
    return { $$type: 'BuyLuckyMegaTicket' as const, ticketId: _ticketId };
}

export function storeTupleBuyLuckyMegaTicket(source: BuyLuckyMegaTicket) {
    const builder = new TupleBuilder();
    builder.writeNumber(source.ticketId);
    return builder.build();
}

export function dictValueParserBuyLuckyMegaTicket(): DictionaryValue<BuyLuckyMegaTicket> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeBuyLuckyMegaTicket(src)).endCell());
        },
        parse: (src) => {
            return loadBuyLuckyMegaTicket(src.loadRef().beginParse());
        }
    }
}

export type MultiLevelLottery$Data = {
    $$type: 'MultiLevelLottery$Data';
    owner: Address;
    basePrice: bigint;
    levelCfg: Dictionary<bigint, LevelConfig>;
    cycles: Dictionary<bigint, Cycle>;
    winningTickets: Dictionary<bigint, WinningTicketsList>;
    players: Dictionary<Address, PlayerInfo>;
    megaJackpot: bigint;
    megaActive: boolean;
    megaCycleId: bigint;
    megaTicketsSold: bigint;
    megaTicketsRequired: bigint;
    megaTickets: Dictionary<bigint, Address>;
    megaTicketPrice: bigint;
    megaPrizeAmount: bigint;
}

export function storeMultiLevelLottery$Data(src: MultiLevelLottery$Data) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeAddress(src.owner);
        b_0.storeInt(src.basePrice, 257);
        b_0.storeDict(src.levelCfg, Dictionary.Keys.BigInt(257), dictValueParserLevelConfig());
        b_0.storeDict(src.cycles, Dictionary.Keys.BigInt(257), dictValueParserCycle());
        const b_1 = new Builder();
        b_1.storeDict(src.winningTickets, Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList());
        b_1.storeDict(src.players, Dictionary.Keys.Address(), dictValueParserPlayerInfo());
        b_1.storeInt(src.megaJackpot, 257);
        b_1.storeBit(src.megaActive);
        b_1.storeInt(src.megaCycleId, 257);
        b_1.storeInt(src.megaTicketsSold, 257);
        const b_2 = new Builder();
        b_2.storeInt(src.megaTicketsRequired, 257);
        b_2.storeDict(src.megaTickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address());
        b_2.storeInt(src.megaTicketPrice, 257);
        b_2.storeInt(src.megaPrizeAmount, 257);
        b_1.storeRef(b_2.endCell());
        b_0.storeRef(b_1.endCell());
    };
}

export function loadMultiLevelLottery$Data(slice: Slice) {
    const sc_0 = slice;
    const _owner = sc_0.loadAddress();
    const _basePrice = sc_0.loadIntBig(257);
    const _levelCfg = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserLevelConfig(), sc_0);
    const _cycles = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserCycle(), sc_0);
    const sc_1 = sc_0.loadRef().beginParse();
    const _winningTickets = Dictionary.load(Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList(), sc_1);
    const _players = Dictionary.load(Dictionary.Keys.Address(), dictValueParserPlayerInfo(), sc_1);
    const _megaJackpot = sc_1.loadIntBig(257);
    const _megaActive = sc_1.loadBit();
    const _megaCycleId = sc_1.loadIntBig(257);
    const _megaTicketsSold = sc_1.loadIntBig(257);
    const sc_2 = sc_1.loadRef().beginParse();
    const _megaTicketsRequired = sc_2.loadIntBig(257);
    const _megaTickets = Dictionary.load(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), sc_2);
    const _megaTicketPrice = sc_2.loadIntBig(257);
    const _megaPrizeAmount = sc_2.loadIntBig(257);
    return { $$type: 'MultiLevelLottery$Data' as const, owner: _owner, basePrice: _basePrice, levelCfg: _levelCfg, cycles: _cycles, winningTickets: _winningTickets, players: _players, megaJackpot: _megaJackpot, megaActive: _megaActive, megaCycleId: _megaCycleId, megaTicketsSold: _megaTicketsSold, megaTicketsRequired: _megaTicketsRequired, megaTickets: _megaTickets, megaTicketPrice: _megaTicketPrice, megaPrizeAmount: _megaPrizeAmount };
}

export function loadTupleMultiLevelLottery$Data(source: TupleReader) {
    const _owner = source.readAddress();
    const _basePrice = source.readBigNumber();
    const _levelCfg = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserLevelConfig(), source.readCellOpt());
    const _cycles = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserCycle(), source.readCellOpt());
    const _winningTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList(), source.readCellOpt());
    const _players = Dictionary.loadDirect(Dictionary.Keys.Address(), dictValueParserPlayerInfo(), source.readCellOpt());
    const _megaJackpot = source.readBigNumber();
    const _megaActive = source.readBoolean();
    const _megaCycleId = source.readBigNumber();
    const _megaTicketsSold = source.readBigNumber();
    const _megaTicketsRequired = source.readBigNumber();
    const _megaTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    const _megaTicketPrice = source.readBigNumber();
    const _megaPrizeAmount = source.readBigNumber();
    return { $$type: 'MultiLevelLottery$Data' as const, owner: _owner, basePrice: _basePrice, levelCfg: _levelCfg, cycles: _cycles, winningTickets: _winningTickets, players: _players, megaJackpot: _megaJackpot, megaActive: _megaActive, megaCycleId: _megaCycleId, megaTicketsSold: _megaTicketsSold, megaTicketsRequired: _megaTicketsRequired, megaTickets: _megaTickets, megaTicketPrice: _megaTicketPrice, megaPrizeAmount: _megaPrizeAmount };
}

export function loadGetterTupleMultiLevelLottery$Data(source: TupleReader) {
    const _owner = source.readAddress();
    const _basePrice = source.readBigNumber();
    const _levelCfg = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserLevelConfig(), source.readCellOpt());
    const _cycles = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserCycle(), source.readCellOpt());
    const _winningTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList(), source.readCellOpt());
    const _players = Dictionary.loadDirect(Dictionary.Keys.Address(), dictValueParserPlayerInfo(), source.readCellOpt());
    const _megaJackpot = source.readBigNumber();
    const _megaActive = source.readBoolean();
    const _megaCycleId = source.readBigNumber();
    const _megaTicketsSold = source.readBigNumber();
    const _megaTicketsRequired = source.readBigNumber();
    const _megaTickets = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.Address(), source.readCellOpt());
    const _megaTicketPrice = source.readBigNumber();
    const _megaPrizeAmount = source.readBigNumber();
    return { $$type: 'MultiLevelLottery$Data' as const, owner: _owner, basePrice: _basePrice, levelCfg: _levelCfg, cycles: _cycles, winningTickets: _winningTickets, players: _players, megaJackpot: _megaJackpot, megaActive: _megaActive, megaCycleId: _megaCycleId, megaTicketsSold: _megaTicketsSold, megaTicketsRequired: _megaTicketsRequired, megaTickets: _megaTickets, megaTicketPrice: _megaTicketPrice, megaPrizeAmount: _megaPrizeAmount };
}

export function storeTupleMultiLevelLottery$Data(source: MultiLevelLottery$Data) {
    const builder = new TupleBuilder();
    builder.writeAddress(source.owner);
    builder.writeNumber(source.basePrice);
    builder.writeCell(source.levelCfg.size > 0 ? beginCell().storeDictDirect(source.levelCfg, Dictionary.Keys.BigInt(257), dictValueParserLevelConfig()).endCell() : null);
    builder.writeCell(source.cycles.size > 0 ? beginCell().storeDictDirect(source.cycles, Dictionary.Keys.BigInt(257), dictValueParserCycle()).endCell() : null);
    builder.writeCell(source.winningTickets.size > 0 ? beginCell().storeDictDirect(source.winningTickets, Dictionary.Keys.BigInt(257), dictValueParserWinningTicketsList()).endCell() : null);
    builder.writeCell(source.players.size > 0 ? beginCell().storeDictDirect(source.players, Dictionary.Keys.Address(), dictValueParserPlayerInfo()).endCell() : null);
    builder.writeNumber(source.megaJackpot);
    builder.writeBoolean(source.megaActive);
    builder.writeNumber(source.megaCycleId);
    builder.writeNumber(source.megaTicketsSold);
    builder.writeNumber(source.megaTicketsRequired);
    builder.writeCell(source.megaTickets.size > 0 ? beginCell().storeDictDirect(source.megaTickets, Dictionary.Keys.BigInt(257), Dictionary.Values.Address()).endCell() : null);
    builder.writeNumber(source.megaTicketPrice);
    builder.writeNumber(source.megaPrizeAmount);
    return builder.build();
}

export function dictValueParserMultiLevelLottery$Data(): DictionaryValue<MultiLevelLottery$Data> {
    return {
        serialize: (src, builder) => {
            builder.storeRef(beginCell().store(storeMultiLevelLottery$Data(src)).endCell());
        },
        parse: (src) => {
            return loadMultiLevelLottery$Data(src.loadRef().beginParse());
        }
    }
}

 type MultiLevelLottery_init_args = {
    $$type: 'MultiLevelLottery_init_args';
    owner: Address;
}

function initMultiLevelLottery_init_args(src: MultiLevelLottery_init_args) {
    return (builder: Builder) => {
        const b_0 = builder;
        b_0.storeAddress(src.owner);
    };
}

async function MultiLevelLottery_init(owner: Address) {
    const __code = Cell.fromHex('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');
    const builder = beginCell();
    builder.storeUint(0, 1);
    initMultiLevelLottery_init_args({ $$type: 'MultiLevelLottery_init_args', owner })(builder);
    const __data = builder.endCell();
    return { code: __code, data: __data };
}

export const MultiLevelLottery_errors = {
    2: { message: "Stack underflow" },
    3: { message: "Stack overflow" },
    4: { message: "Integer overflow" },
    5: { message: "Integer out of expected range" },
    6: { message: "Invalid opcode" },
    7: { message: "Type check error" },
    8: { message: "Cell overflow" },
    9: { message: "Cell underflow" },
    10: { message: "Dictionary error" },
    11: { message: "'Unknown' error" },
    12: { message: "Fatal error" },
    13: { message: "Out of gas error" },
    14: { message: "Virtualization error" },
    32: { message: "Action list is invalid" },
    33: { message: "Action list is too long" },
    34: { message: "Action is invalid or not supported" },
    35: { message: "Invalid source address in outbound message" },
    36: { message: "Invalid destination address in outbound message" },
    37: { message: "Not enough Toncoin" },
    38: { message: "Not enough extra currencies" },
    39: { message: "Outbound message does not fit into a cell after rewriting" },
    40: { message: "Cannot process a message" },
    41: { message: "Library reference is null" },
    42: { message: "Library change action error" },
    43: { message: "Exceeded maximum number of cells in the library or the maximum depth of the Merkle tree" },
    50: { message: "Account state size exceeded limits" },
    128: { message: "Null reference exception" },
    129: { message: "Invalid serialization prefix" },
    130: { message: "Invalid incoming message" },
    131: { message: "Constraints error" },
    132: { message: "Access denied" },
    133: { message: "Contract stopped" },
    134: { message: "Invalid argument" },
    135: { message: "Code of a contract was not found" },
    136: { message: "Invalid standard address" },
    138: { message: "Not a basechain address" },
    2404: { message: "BAD_OWNER" },
    3926: { message: "NOT_ENOUGH_TON" },
    13001: { message: "MEGA_NOT_ACTIVE" },
    13082: { message: "NO_MEGA_WINNER" },
    15380: { message: "MEGA_ALREADY_ACTIVE" },
    15475: { message: "TOO_MANY_MEGA_TICKETS" },
    15618: { message: "INVALID_MEGA_TICKET_ID" },
    20915: { message: "CYCLE_FINISHED" },
    23656: { message: "NOT_ENOUGH_MEGA_TICKETS_AVAILABLE" },
    24082: { message: "NOT_ENOUGH_AVAILABLE_TICKETS" },
    24243: { message: "BAD_MULT" },
    29392: { message: "BAD_AMOUNT" },
    31794: { message: "BAD_PRIZE_AMOUNT" },
    33070: { message: "BAD_COUNT" },
    35867: { message: "BAD_PRICE" },
    37469: { message: "ONLY_OWNER" },
    49061: { message: "TICKET_ALREADY_TAKEN" },
    53267: { message: "ALREADY_REGISTERED" },
    56464: { message: "BAD_TICKET_COUNT" },
    57100: { message: "MEGA_TICKET_ALREADY_TAKEN" },
    58403: { message: "INVALID_TICKET_ID" },
    61473: { message: "NO_CYCLE" },
    62110: { message: "BAD_LEVEL" },
    62587: { message: "TOO_MANY_TICKETS" },
} as const

export const MultiLevelLottery_errors_backward = {
    "Stack underflow": 2,
    "Stack overflow": 3,
    "Integer overflow": 4,
    "Integer out of expected range": 5,
    "Invalid opcode": 6,
    "Type check error": 7,
    "Cell overflow": 8,
    "Cell underflow": 9,
    "Dictionary error": 10,
    "'Unknown' error": 11,
    "Fatal error": 12,
    "Out of gas error": 13,
    "Virtualization error": 14,
    "Action list is invalid": 32,
    "Action list is too long": 33,
    "Action is invalid or not supported": 34,
    "Invalid source address in outbound message": 35,
    "Invalid destination address in outbound message": 36,
    "Not enough Toncoin": 37,
    "Not enough extra currencies": 38,
    "Outbound message does not fit into a cell after rewriting": 39,
    "Cannot process a message": 40,
    "Library reference is null": 41,
    "Library change action error": 42,
    "Exceeded maximum number of cells in the library or the maximum depth of the Merkle tree": 43,
    "Account state size exceeded limits": 50,
    "Null reference exception": 128,
    "Invalid serialization prefix": 129,
    "Invalid incoming message": 130,
    "Constraints error": 131,
    "Access denied": 132,
    "Contract stopped": 133,
    "Invalid argument": 134,
    "Code of a contract was not found": 135,
    "Invalid standard address": 136,
    "Not a basechain address": 138,
    "BAD_OWNER": 2404,
    "NOT_ENOUGH_TON": 3926,
    "MEGA_NOT_ACTIVE": 13001,
    "NO_MEGA_WINNER": 13082,
    "MEGA_ALREADY_ACTIVE": 15380,
    "TOO_MANY_MEGA_TICKETS": 15475,
    "INVALID_MEGA_TICKET_ID": 15618,
    "CYCLE_FINISHED": 20915,
    "NOT_ENOUGH_MEGA_TICKETS_AVAILABLE": 23656,
    "NOT_ENOUGH_AVAILABLE_TICKETS": 24082,
    "BAD_MULT": 24243,
    "BAD_AMOUNT": 29392,
    "BAD_PRIZE_AMOUNT": 31794,
    "BAD_COUNT": 33070,
    "BAD_PRICE": 35867,
    "ONLY_OWNER": 37469,
    "TICKET_ALREADY_TAKEN": 49061,
    "ALREADY_REGISTERED": 53267,
    "BAD_TICKET_COUNT": 56464,
    "MEGA_TICKET_ALREADY_TAKEN": 57100,
    "INVALID_TICKET_ID": 58403,
    "NO_CYCLE": 61473,
    "BAD_LEVEL": 62110,
    "TOO_MANY_TICKETS": 62587,
} as const

const MultiLevelLottery_types: ABIType[] = [
    {"name":"DataSize","header":null,"fields":[{"name":"cells","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"bits","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"refs","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"SignedBundle","header":null,"fields":[{"name":"signature","type":{"kind":"simple","type":"fixed-bytes","optional":false,"format":64}},{"name":"signedData","type":{"kind":"simple","type":"slice","optional":false,"format":"remainder"}}]},
    {"name":"StateInit","header":null,"fields":[{"name":"code","type":{"kind":"simple","type":"cell","optional":false}},{"name":"data","type":{"kind":"simple","type":"cell","optional":false}}]},
    {"name":"Context","header":null,"fields":[{"name":"bounceable","type":{"kind":"simple","type":"bool","optional":false}},{"name":"sender","type":{"kind":"simple","type":"address","optional":false}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"raw","type":{"kind":"simple","type":"slice","optional":false}}]},
    {"name":"SendParameters","header":null,"fields":[{"name":"mode","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"body","type":{"kind":"simple","type":"cell","optional":true}},{"name":"code","type":{"kind":"simple","type":"cell","optional":true}},{"name":"data","type":{"kind":"simple","type":"cell","optional":true}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"to","type":{"kind":"simple","type":"address","optional":false}},{"name":"bounce","type":{"kind":"simple","type":"bool","optional":false}}]},
    {"name":"MessageParameters","header":null,"fields":[{"name":"mode","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"body","type":{"kind":"simple","type":"cell","optional":true}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"to","type":{"kind":"simple","type":"address","optional":false}},{"name":"bounce","type":{"kind":"simple","type":"bool","optional":false}}]},
    {"name":"DeployParameters","header":null,"fields":[{"name":"mode","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"body","type":{"kind":"simple","type":"cell","optional":true}},{"name":"value","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"bounce","type":{"kind":"simple","type":"bool","optional":false}},{"name":"init","type":{"kind":"simple","type":"StateInit","optional":false}}]},
    {"name":"StdAddress","header":null,"fields":[{"name":"workchain","type":{"kind":"simple","type":"int","optional":false,"format":8}},{"name":"address","type":{"kind":"simple","type":"uint","optional":false,"format":256}}]},
    {"name":"VarAddress","header":null,"fields":[{"name":"workchain","type":{"kind":"simple","type":"int","optional":false,"format":32}},{"name":"address","type":{"kind":"simple","type":"slice","optional":false}}]},
    {"name":"BasechainAddress","header":null,"fields":[{"name":"hash","type":{"kind":"simple","type":"int","optional":true,"format":257}}]},
    {"name":"Deploy","header":2490013878,"fields":[{"name":"queryId","type":{"kind":"simple","type":"uint","optional":false,"format":64}}]},
    {"name":"DeployOk","header":2952335191,"fields":[{"name":"queryId","type":{"kind":"simple","type":"uint","optional":false,"format":64}}]},
    {"name":"FactoryDeploy","header":1829761339,"fields":[{"name":"queryId","type":{"kind":"simple","type":"uint","optional":false,"format":64}},{"name":"cashback","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerSetBasePrice","header":1268395613,"fields":[{"name":"newPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"OwnerSetLevelMultiplier","header":2399467553,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"k","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"OwnerWithdraw","header":2082387357,"fields":[{"name":"amount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"recipient","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerTransferOwnership","header":2073089375,"fields":[{"name":"newOwner","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"OwnerActivateMega","header":2107791225,"fields":[{"name":"ticketCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"prizeAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"LevelConfig","header":null,"fields":[{"name":"k","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"Cycle","header":null,"fields":[{"name":"id","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsSold","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"isFinished","type":{"kind":"simple","type":"bool","optional":false}},{"name":"tickets","type":{"kind":"dict","key":"int","value":"address"}}]},
    {"name":"UserTickets","header":null,"fields":[{"name":"cycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsOwned","type":{"kind":"dict","key":"int","value":"bool"}}]},
    {"name":"PlayerInfo","header":null,"fields":[{"name":"referrer","type":{"kind":"simple","type":"address","optional":false}},{"name":"userLevel","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"winningJackpotAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsBought","type":{"kind":"dict","key":"int","value":"UserTickets","valueFormat":"ref"}},{"name":"referredCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"consecutiveWins","type":{"kind":"dict","key":"int","value":"int"}},{"name":"lastWinCycle","type":{"kind":"dict","key":"int","value":"int"}},{"name":"inRowBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"buyBonus","type":{"kind":"dict","key":"int","value":"int"}},{"name":"soldBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"referralBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsSoldByReferrals","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketsBought","type":{"kind":"simple","type":"UserTickets","optional":false}}]},
    {"name":"WinningTicketsList","header":null,"fields":[{"name":"winningTicketIds","type":{"kind":"dict","key":"int","value":"int"}},{"name":"winningTicketAmounts","type":{"kind":"dict","key":"int","value":"int"}}]},
    {"name":"PlayerBasicInfo","header":null,"fields":[{"name":"exists","type":{"kind":"simple","type":"bool","optional":false}},{"name":"userLevel","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"winningJackpotAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"referredCount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"inRowBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"soldBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"referralBonus","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsSoldByReferrals","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"PlayerTicketsInfo","header":null,"fields":[{"name":"exists","type":{"kind":"simple","type":"bool","optional":false}},{"name":"cycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"MegaJackpotInfo","header":null,"fields":[{"name":"balance","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"isActive","type":{"kind":"simple","type":"bool","optional":false}},{"name":"ticketsSold","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketsRequired","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"prizeAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"threshold","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"Register","header":2206064315,"fields":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"referrer","type":{"kind":"simple","type":"address","optional":false}}]},
    {"name":"BuyTicket","header":264192202,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"BuyLuckyTicket","header":1049254189,"fields":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"BuyMegaTicket","header":764815221,"fields":[{"name":"count","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"BuyLuckyMegaTicket","header":4112755696,"fields":[{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
    {"name":"MultiLevelLottery$Data","header":null,"fields":[{"name":"owner","type":{"kind":"simple","type":"address","optional":false}},{"name":"basePrice","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"levelCfg","type":{"kind":"dict","key":"int","value":"LevelConfig","valueFormat":"ref"}},{"name":"cycles","type":{"kind":"dict","key":"int","value":"Cycle","valueFormat":"ref"}},{"name":"winningTickets","type":{"kind":"dict","key":"int","value":"WinningTicketsList","valueFormat":"ref"}},{"name":"players","type":{"kind":"dict","key":"address","value":"PlayerInfo","valueFormat":"ref"}},{"name":"megaJackpot","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaActive","type":{"kind":"simple","type":"bool","optional":false}},{"name":"megaCycleId","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketsSold","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTicketsRequired","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaTickets","type":{"kind":"dict","key":"int","value":"address"}},{"name":"megaTicketPrice","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"megaPrizeAmount","type":{"kind":"simple","type":"int","optional":false,"format":257}}]},
]

const MultiLevelLottery_opcodes = {
    "Deploy": 2490013878,
    "DeployOk": 2952335191,
    "FactoryDeploy": 1829761339,
    "OwnerSetBasePrice": 1268395613,
    "OwnerSetLevelMultiplier": 2399467553,
    "OwnerWithdraw": 2082387357,
    "OwnerTransferOwnership": 2073089375,
    "OwnerActivateMega": 2107791225,
    "Register": 2206064315,
    "BuyTicket": 264192202,
    "BuyLuckyTicket": 1049254189,
    "BuyMegaTicket": 764815221,
    "BuyLuckyMegaTicket": 4112755696,
}

const MultiLevelLottery_getters: ABIGetter[] = [
    {"name":"getPlayerBasicInfo","methodId":82618,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"simple","type":"PlayerBasicInfo","optional":false}},
    {"name":"getPlayerReferrer","methodId":90665,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"simple","type":"address","optional":true}},
    {"name":"getPlayerTicketsInLevel","methodId":115408,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"PlayerTicketsInfo","optional":false}},
    {"name":"getPlayerMegaTickets","methodId":113255,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"simple","type":"PlayerTicketsInfo","optional":false}},
    {"name":"getUserMegaTicketIds","methodId":109617,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}}],"returnType":{"kind":"dict","key":"int","value":"int"}},
    {"name":"hasPlayerMegaTicket","methodId":120050,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getPlayerConsecutiveWins","methodId":73091,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getPlayerLastWinCycle","methodId":73653,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getPlayerBuyBonus","methodId":122736,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"hasPlayerTicket","methodId":117898,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getUserTicketIds","methodId":85391,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"dict","key":"int","value":"int"}},
    {"name":"getUserTicketCount","methodId":124880,"arguments":[{"name":"user","type":{"kind":"simple","type":"address","optional":false}},{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getCycle","methodId":81383,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"Cycle","optional":true}},
    {"name":"getLevelConfig","methodId":78942,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"LevelConfig","optional":true}},
    {"name":"getLevelPrice","methodId":99207,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getWinningTickets","methodId":120154,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"WinningTicketsList","optional":true}},
    {"name":"getMegaJackpot","methodId":91237,"arguments":[],"returnType":{"kind":"simple","type":"int","optional":false,"format":257}},
    {"name":"getOwner","methodId":102025,"arguments":[],"returnType":{"kind":"simple","type":"address","optional":false}},
    {"name":"isTicketAvailable","methodId":108921,"arguments":[{"name":"level","type":{"kind":"simple","type":"int","optional":false,"format":257}},{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getMegaJackpotInfo","methodId":115452,"arguments":[],"returnType":{"kind":"simple","type":"MegaJackpotInfo","optional":false}},
    {"name":"isMegaTicketAvailable","methodId":111834,"arguments":[{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"bool","optional":false}},
    {"name":"getMegaTicketOwner","methodId":123239,"arguments":[{"name":"ticketId","type":{"kind":"simple","type":"int","optional":false,"format":257}}],"returnType":{"kind":"simple","type":"address","optional":true}},
]

export const MultiLevelLottery_getterMapping: { [key: string]: string } = {
    'getPlayerBasicInfo': 'getGetPlayerBasicInfo',
    'getPlayerReferrer': 'getGetPlayerReferrer',
    'getPlayerTicketsInLevel': 'getGetPlayerTicketsInLevel',
    'getPlayerMegaTickets': 'getGetPlayerMegaTickets',
    'getUserMegaTicketIds': 'getGetUserMegaTicketIds',
    'hasPlayerMegaTicket': 'getHasPlayerMegaTicket',
    'getPlayerConsecutiveWins': 'getGetPlayerConsecutiveWins',
    'getPlayerLastWinCycle': 'getGetPlayerLastWinCycle',
    'getPlayerBuyBonus': 'getGetPlayerBuyBonus',
    'hasPlayerTicket': 'getHasPlayerTicket',
    'getUserTicketIds': 'getGetUserTicketIds',
    'getUserTicketCount': 'getGetUserTicketCount',
    'getCycle': 'getGetCycle',
    'getLevelConfig': 'getGetLevelConfig',
    'getLevelPrice': 'getGetLevelPrice',
    'getWinningTickets': 'getGetWinningTickets',
    'getMegaJackpot': 'getGetMegaJackpot',
    'getOwner': 'getGetOwner',
    'isTicketAvailable': 'getIsTicketAvailable',
    'getMegaJackpotInfo': 'getGetMegaJackpotInfo',
    'isMegaTicketAvailable': 'getIsMegaTicketAvailable',
    'getMegaTicketOwner': 'getGetMegaTicketOwner',
}

const MultiLevelLottery_receivers: ABIReceiver[] = [
    {"receiver":"internal","message":{"kind":"typed","type":"Register"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyLuckyTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyMegaTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"BuyLuckyMegaTicket"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerSetBasePrice"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerSetLevelMultiplier"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerWithdraw"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerTransferOwnership"}},
    {"receiver":"internal","message":{"kind":"typed","type":"OwnerActivateMega"}},
    {"receiver":"internal","message":{"kind":"typed","type":"Deploy"}},
]

export const LEVEL_MINING = 0n;
export const LEVEL_GOLD = 1n;
export const LEVEL_TREASURE = 2n;
export const MAX_TICKETS = 777n;
export const JACKPOT_MULT = 100n;
export const MULT_4X_COUNT = 16n;
export const MULT_2X_COUNT = 62n;
export const MULT_1X_COUNT = 125n;
export const TOTAL_WINNERS = 204n;
export const BNSS_FOR10_REF = 100000n;
export const BNSS_FOR3_WINS = 1000000n;
export const MEGA_THRESHOLD = 2500000000n;
export const MEGA_TICKET_PRICE = 1000000n;

export class MultiLevelLottery implements Contract {
    
    public static readonly storageReserve = 0n;
    public static readonly errors = MultiLevelLottery_errors_backward;
    public static readonly opcodes = MultiLevelLottery_opcodes;
    
    static async init(owner: Address) {
        return await MultiLevelLottery_init(owner);
    }
    
    static async fromInit(owner: Address) {
        const __gen_init = await MultiLevelLottery_init(owner);
        const address = contractAddress(0, __gen_init);
        return new MultiLevelLottery(address, __gen_init);
    }
    
    static fromAddress(address: Address) {
        return new MultiLevelLottery(address);
    }
    
    readonly address: Address; 
    readonly init?: { code: Cell, data: Cell };
    readonly abi: ContractABI = {
        types:  MultiLevelLottery_types,
        getters: MultiLevelLottery_getters,
        receivers: MultiLevelLottery_receivers,
        errors: MultiLevelLottery_errors,
    };
    
    constructor(address: Address, init?: { code: Cell, data: Cell }) {
        this.address = address;
        this.init = init;
    }
    
    async send(provider: ContractProvider, via: Sender, args: { value: bigint, bounce?: boolean| null | undefined }, message: Register | BuyLuckyTicket | BuyTicket | BuyMegaTicket | BuyLuckyMegaTicket | OwnerSetBasePrice | OwnerSetLevelMultiplier | OwnerWithdraw | OwnerTransferOwnership | OwnerActivateMega | Deploy) {
        
        let body: Cell | null = null;
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'Register') {
            body = beginCell().store(storeRegister(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyLuckyTicket') {
            body = beginCell().store(storeBuyLuckyTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyTicket') {
            body = beginCell().store(storeBuyTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyMegaTicket') {
            body = beginCell().store(storeBuyMegaTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'BuyLuckyMegaTicket') {
            body = beginCell().store(storeBuyLuckyMegaTicket(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerSetBasePrice') {
            body = beginCell().store(storeOwnerSetBasePrice(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerSetLevelMultiplier') {
            body = beginCell().store(storeOwnerSetLevelMultiplier(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerWithdraw') {
            body = beginCell().store(storeOwnerWithdraw(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerTransferOwnership') {
            body = beginCell().store(storeOwnerTransferOwnership(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'OwnerActivateMega') {
            body = beginCell().store(storeOwnerActivateMega(message)).endCell();
        }
        if (message && typeof message === 'object' && !(message instanceof Slice) && message.$$type === 'Deploy') {
            body = beginCell().store(storeDeploy(message)).endCell();
        }
        if (body === null) { throw new Error('Invalid message type'); }
        
        await provider.internal(via, { ...args, body: body });
        
    }
    
    async getGetPlayerBasicInfo(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getPlayerBasicInfo', builder.build())).stack;
        const result = loadGetterTuplePlayerBasicInfo(source);
        return result;
    }
    
    async getGetPlayerReferrer(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getPlayerReferrer', builder.build())).stack;
        const result = source.readAddressOpt();
        return result;
    }
    
    async getGetPlayerTicketsInLevel(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerTicketsInLevel', builder.build())).stack;
        const result = loadGetterTuplePlayerTicketsInfo(source);
        return result;
    }
    
    async getGetPlayerMegaTickets(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getPlayerMegaTickets', builder.build())).stack;
        const result = loadGetterTuplePlayerTicketsInfo(source);
        return result;
    }
    
    async getGetUserMegaTicketIds(provider: ContractProvider, user: Address) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        const source = (await provider.get('getUserMegaTicketIds', builder.build())).stack;
        const result = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
        return result;
    }
    
    async getHasPlayerMegaTicket(provider: ContractProvider, user: Address, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(ticketId);
        const source = (await provider.get('hasPlayerMegaTicket', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetPlayerConsecutiveWins(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerConsecutiveWins', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetPlayerLastWinCycle(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerLastWinCycle', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetPlayerBuyBonus(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getPlayerBuyBonus', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getHasPlayerTicket(provider: ContractProvider, user: Address, level: bigint, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        builder.writeNumber(ticketId);
        const source = (await provider.get('hasPlayerTicket', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetUserTicketIds(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getUserTicketIds', builder.build())).stack;
        const result = Dictionary.loadDirect(Dictionary.Keys.BigInt(257), Dictionary.Values.BigInt(257), source.readCellOpt());
        return result;
    }
    
    async getGetUserTicketCount(provider: ContractProvider, user: Address, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeAddress(user);
        builder.writeNumber(level);
        const source = (await provider.get('getUserTicketCount', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetCycle(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getCycle', builder.build())).stack;
        const result_p = source.readTupleOpt();
        const result = result_p ? loadTupleCycle(result_p) : null;
        return result;
    }
    
    async getGetLevelConfig(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getLevelConfig', builder.build())).stack;
        const result_p = source.readTupleOpt();
        const result = result_p ? loadTupleLevelConfig(result_p) : null;
        return result;
    }
    
    async getGetLevelPrice(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getLevelPrice', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetWinningTickets(provider: ContractProvider, level: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        const source = (await provider.get('getWinningTickets', builder.build())).stack;
        const result_p = source.readTupleOpt();
        const result = result_p ? loadTupleWinningTicketsList(result_p) : null;
        return result;
    }
    
    async getGetMegaJackpot(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getMegaJackpot', builder.build())).stack;
        const result = source.readBigNumber();
        return result;
    }
    
    async getGetOwner(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getOwner', builder.build())).stack;
        const result = source.readAddress();
        return result;
    }
    
    async getIsTicketAvailable(provider: ContractProvider, level: bigint, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(level);
        builder.writeNumber(ticketId);
        const source = (await provider.get('isTicketAvailable', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetMegaJackpotInfo(provider: ContractProvider) {
        const builder = new TupleBuilder();
        const source = (await provider.get('getMegaJackpotInfo', builder.build())).stack;
        const result = loadGetterTupleMegaJackpotInfo(source);
        return result;
    }
    
    async getIsMegaTicketAvailable(provider: ContractProvider, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(ticketId);
        const source = (await provider.get('isMegaTicketAvailable', builder.build())).stack;
        const result = source.readBoolean();
        return result;
    }
    
    async getGetMegaTicketOwner(provider: ContractProvider, ticketId: bigint) {
        const builder = new TupleBuilder();
        builder.writeNumber(ticketId);
        const source = (await provider.get('getMegaTicketOwner', builder.build())).stack;
        const result = source.readAddressOpt();
        return result;
    }
    
}